import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { User } from '../../../_models/user';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { API_URLS } from '../../../config/api.urls';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-delivery-request-detail',
    templateUrl: './delivery-request-detail.component.html',
    styleUrls: ['./delivery-request-detail.component.css'],
    standalone: false
})
export class DeliveryRequestDetailComponent implements OnInit, OnDestroy {

    loading = true;
    request: any = null;
    requestId: string;
    user: User;
    lang = 'fr';
    updating = false;
    selectedStatus = '';
    description = '';

    deliveryStatuses = [
        { value: 'IN_PREPARATION', labelKey: 'delivery.status_in_preparation' },
        { value: 'IN_TRANSIT', labelKey: 'tracking.step_in_transit' },
        { value: 'IN_DELIVERING', labelKey: 'delivery.status_in_delivering' },
        { value: 'DELIVERED', labelKey: 'tracking.step_delivered' },
        { value: 'FAILED', labelKey: 'delivery.status_failed' }
    ];

    private subscriptions: Subscription[] = [];

    constructor(
        private titleService: Title,
        private route: ActivatedRoute,
        private router: Router,
        private httpClient: HttpClient,
        private translateService: TranslateService,
        private toastr: ToastrService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.requestId = this.route.snapshot.paramMap.get('id') ?? '';
        this.lang = this.translateService.currentLang;

        if (!this.requestId) {
            this.router.navigate(['not-found']);
        }
    }

    ngOnInit() {
        this.loadRequest();
    }

    loadRequest() {
        this.loading = true;
        const sub = this.httpClient.get<any>(API_URLS.DELIVERY_REQUESTS_BASE_URL + '/' + this.requestId)
            .subscribe({
                next: (res: any) => {
                    this.request = res.data;
                    this.selectedStatus = this.request.deliveryStatus;
                    this.titleService.setTitle('Delivery Request #' + this.request.deliveringRef + ' - ' + environment.appTitle);
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.router.navigate(['not-found']);
                }
            });
        this.subscriptions.push(sub);
    }

    updateStatus() {
        if (!this.selectedStatus || this.updating) return;
        this.updating = true;

        const payload: any = { status: this.selectedStatus };
        if (this.description) {
            payload.description = this.description;
        }

        const sub = this.httpClient.put<any>(
            API_URLS.DELIVERY_REQUESTS_BASE_URL + '/' + this.requestId + '/changeStatus',
            payload
        ).subscribe({
            next: (res: any) => {
                this.updating = false;
                this.toastr.success(res.message || 'Status updated');
                this.description = '';
                this.request.deliveryStatus = this.selectedStatus;
            },
            error: () => {
                this.updating = false;
                this.toastr.error('Failed to update status. Please try again.');
            }
        });
        this.subscriptions.push(sub);
    }

    getProductName(): string {
        if (!this.request?.product) return '';
        return this.lang === 'fr' && this.request.product.productNameFr
            ? this.request.product.productNameFr
            : this.request.product.productName;
    }

    productPictureUrl(): string {
        const picture = this.request?.product?.picture || this.request?.product?.defaultPicture;
        if (!picture) return '';
        return environment.image_cdn_url + '/' + picture;
    }

    pickupAddressString(): string {
        const addr = this.request?.pickupAddress;
        if (!addr) return '';
        return [addr.addressLine1, addr.addressLine2, addr.town, addr.country]
            .filter((v: string) => v).join(', ');
    }

    deliveryAddressString(): string {
        const addr = this.request?.deliveryAddress;
        if (!addr) return '';
        const country = addr.country?.name || addr.country || '';
        return [addr.addressLine1, addr.addressLine2, addr.town, country]
            .filter((v: string) => v).join(', ');
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
        return date.toLocaleDateString(this.lang === 'fr' ? 'fr-FR' : 'en-US', options);
    }

    formatCurrency(amount: number): string {
        if (amount == null) return '';
        return new Intl.NumberFormat(this.lang === 'fr' ? 'fr-FR' : 'en-US').format(amount) + ' XAF';
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'PENDING': return 'badge bg-warning text-dark';
            case 'CONFIRMED': return 'badge bg-primary';
            case 'IN_PREPARATION': return 'badge bg-info';
            case 'IN_TRANSIT': return 'badge bg-purple';
            case 'IN_DELIVERING': return 'badge bg-primary';
            case 'DELIVERED': return 'badge bg-success';
            case 'FAILED': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    getDeliveryStatusLabel(status: string): string {
        if (!status) return '';
        return 'delivery.status_' + status.toLowerCase();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

}
